import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { Dialog, Button as MuiButton, TextField, IconButton, InputAdornment } from "@mui/material";
// import Button from "@mui/material/Button";
// import { PlusIcon } from "@heroicons/react/24/outline";
import Pagination from "@mui/material/Pagination";
import MaterialSelectionModal from "../components/MaterialSelectionModal";
import { getApiUrl } from "../config/api";
// import { checkTokenValidity } from "../utils/auth";
// import LoadingScreen from "../components/LoadingScreen";
import { PulseLoader } from "react-spinners";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { LockClosedIcon as LockIcon } from "@heroicons/react/24/solid";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const Home = () => {
	const navigate = useNavigate();
	const [spareparts, setSpareparts] = useState([]);
	const [error, setError] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10); // Number of items to show per page
	const [materialModalVisible, setMaterialModalVisible] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // Only for search/refresh operations
	const [returModalOpen, setReturModalOpen] = useState(false);
	const [returItems, setReturItems] = useState([]);
	const [returSearchQuery, setReturSearchQuery] = useState("");
	const [returLoading, setReturLoading] = useState(false);
	const [returCurrentPage, setReturCurrentPage] = useState(1);
	const [returItemsPerPage] = useState(10);
	const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
	const [passwordForm, setPasswordForm] = useState({
		current_password: '',
		new_password: '',
		new_password_confirmation: ''
	});
	const [passwordErrors, setPasswordErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [passwordSuccess, setPasswordSuccess] = useState('');
	const [showPasswords, setShowPasswords] = useState({
		current: false,
		new: false,
		confirm: false
	});

	useEffect(() => {
		const fetchSpareparts = async () => {
			try {
				setIsLoading(true);
				const response = await axios.get(getApiUrl("/spareparts"), {
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				});
				setSpareparts(response.data);
			} catch (error) {
				console.error("Error fetching spareparts:", error);
				setError("Failed to fetch spareparts");
				if (error.response?.status === 401) {
					localStorage.removeItem("token");
					navigate("/login");
				}
			} finally {
				setIsLoading(false);
			}
		};

		fetchSpareparts();
	}, [navigate]);

	if (error) {
		return <div className="text-center text-red-600 py-4">{error}</div>;
	}

	// Filter spareparts based on search query
	const filteredSpareparts = spareparts.filter((part) => {
		const query = searchQuery.toLowerCase();
		return (
			part.inqno.toLowerCase().includes(query) ||
			(part.matnr && part.matnr.toLowerCase().includes(query)) ||
			(part.maktx && part.maktx.toLowerCase().includes(query))
		);
	});
		// part.inqno.toLowerCase().includes(searchQuery.toLowerCase())

	// Calculate pagination values
	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredSpareparts.slice(
		indexOfFirstItem,
		indexOfLastItem
	);
	const totalPages = Math.ceil(filteredSpareparts.length / itemsPerPage);

	const formatInqNo = (inqno) => {
		// If the inqno is purely numeric, remove leading zeros
		if (/^\d+$/.test(inqno)) {
			return parseInt(inqno, 10).toString();
		}
		// Otherwise return as is
		return inqno.toString();
	};

	const formatDateTime = (date, time) => {
		if (!date) return "-";
		const [year, month, day] = date.split("-");
		return `${day}-${month}-${year} ${time}`;
	};

	const handleLogout = async () => {
		try {
			await axios.post(
				getApiUrl("/logout"),
				{},
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);
			localStorage.removeItem("token");
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	// const handleAddClick = () => {
	// 	setOpenDialog(true);
	// };

	const handleCategorySelect = (category) => {
		setOpenDialog(false);
		navigate("/add-sparepart", { state: { category } });
	};

	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};

	const handleSearch = async (searchTerm) => {
		try {
			setIsLoading(true);
			const response = await axios.get(
				getApiUrl(`/spareparts/search?q=${searchTerm}`),
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);
			setSpareparts(response.data);
		} catch (error) {
			console.error("Error searching spareparts:", error);
			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleMaterialSelect = (selectedMaterials, type, category) => {
		setMaterialModalVisible(false);
		navigate("/add-sparepart", {
			state: {
				materials: selectedMaterials,
				type: type, // "V" or "NV"
				category: category, // "IO" or "AG" for type V, null for type NV
			},
		});
	};

	const handleSearchInputChange = (e) => {
		setSearchQuery(e.target.value);
		setReturCurrentPage(1); // Reset to first page when searching
		// Debounce the API call to avoid too many requests
		const timeoutId = setTimeout(() => {
			handleSearch(e.target.value);
		}, 500);
		return () => clearTimeout(timeoutId);
	};

	const handlePengembalian = () => {
		setReturModalOpen(true);
		setReturCurrentPage(1); // Reset to first page
		fetchReturItems();
	};

	const fetchReturItems = async () => {
		try {
			setReturLoading(true);
			const response = await axios.get(getApiUrl("/spareparts-ret"), {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			});
			
			// Group items by inqno and combine their maktx values
			const groupedItems = response.data.reduce((acc, current) => {
				const existingItem = acc.find(item => item.inqno === current.inqno);
				if (!existingItem) {
					acc.push({
						...current,
						allMaktx: [current.maktx] // Store all maktx values in an array
					});
				} else {
					// Add new maktx if it doesn't exist
					if (!existingItem.allMaktx.includes(current.maktx)) {
						existingItem.allMaktx.push(current.maktx);
					}
				}
				return acc;
			}, []);
			
			setReturItems(groupedItems);
		} catch (error) {
			console.error("Error fetching retur items:", error);
			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setReturLoading(false);
		}
	};

	const handleReturSelect = (inqno, type) => {
		setReturModalOpen(false);
		// Determine category based on type
		let category;
		if (type === 'AG') {
			category = 'RAG';
		} else if (type === 'IO') {
			category = 'RIO';
		} else if (type === 'CGI') {
			category = 'CRT';
		}
		navigate("/addretur", { state: { inqno, category } });
	};

	// Filter retur items based on search - now includes all maktx values
	const filteredReturItems = returItems.filter((item) => {
		if (!returSearchQuery) return true;
		
		const searchLower = returSearchQuery.toLowerCase().trim();
		const inqnoMatch = item.inqno?.toLowerCase().includes(searchLower);
		const maktxMatch = item.allMaktx?.some(maktx => 
			maktx?.toLowerCase().includes(searchLower)
		);
		const currentMaktxMatch = item.maktx?.toLowerCase().includes(searchLower);
		
		return inqnoMatch || maktxMatch || currentMaktxMatch;
	});

	// Update the filteredReturItems pagination logic
	const returIndexOfLastItem = returCurrentPage * returItemsPerPage;
	const returIndexOfFirstItem = returIndexOfLastItem - returItemsPerPage;
	const currentReturItems = filteredReturItems.slice(
		returIndexOfFirstItem,
		returIndexOfLastItem
	);
	const returTotalPages = Math.ceil(filteredReturItems.length / returItemsPerPage);

	const handleReturPageChange = (event, value) => {
		setReturCurrentPage(value);
	};

	const handleReturSearch = (e) => {
		setReturSearchQuery(e.target.value);
		setReturCurrentPage(1); // Reset to first page when searching
	};

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setPasswordErrors({});
		setPasswordSuccess('');
		setIsSubmitting(true);

		try {
			await axios.post(
				getApiUrl("/change-password"),
				passwordForm,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);
			setPasswordSuccess('Password changed successfully. You will be redirected to login...');
			// Clear form but don't close modal yet
			setPasswordForm({
				current_password: '',
				new_password: '',
				new_password_confirmation: ''
			});
			// Wait 2 seconds then logout
			setTimeout(() => {
				handleLogout();
			}, 2000);
		} catch (error) {
			if (error.response?.data?.errors) {
				setPasswordErrors(error.response.data.errors);
			} else {
				setPasswordErrors({
					general: ['An unexpected error occurred. Please try again.']
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlePasswordInputChange = (field) => (e) => {
		setPasswordForm(prev => ({
			...prev,
			[field]: e.target.value
		}));
	};

	const togglePasswordVisibility = (field) => {
		setShowPasswords(prev => ({
			...prev,
			[field]: !prev[field]
		}));
	};

	return (
		<div className="min-h-screen bg-gray-100">
			<nav className="bg-white shadow-lg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						<div className="flex items-center">
							<h1 className="text-xl font-bold text-gray-900">
								BBMA Sparepart
							</h1>
						</div>
						<div className="flex items-center gap-2">
							<button
								onClick={() => setChangePasswordModalOpen(true)}
								className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							>
								Change Password
							</button>
							<button
								onClick={handleLogout}
								className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
							>
								Logout
							</button>
						</div>
					</div>
				</div>
			</nav>

			<main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
				<div className="px-4 py-6 sm:px-0">
					<div className="bg-white shadow-sm rounded-lg">
						<div className="p-4">
							{/* Desktop view - title and buttons */}
							<div className="hidden md:flex justify-between items-center mb-4">
								<h2 className="text-2xl font-bold text-gray-900">
									List Pengambilan dan Pengembalian Barang
								</h2>
								<div className="flex gap-2">
									<MuiButton
										variant="contained"
										color="primary"
										size="small"
										onClick={() => setMaterialModalVisible(true)}
										sx={{ textTransform: "none" }}
									>
										Pengambilan Barang
									</MuiButton>
									<MuiButton
										variant="contained"
										color="error"
										size="small"
										onClick={handlePengembalian}
										sx={{ textTransform: "none" }}
									>
										Pengembalian Barang
									</MuiButton>
								</div>
							</div>

							{/* Mobile view - buttons */}
							<div className="md:hidden flex flex-col gap-2 mb-4">
								<div className="flex flex-col gap-2">
									<MuiButton
										variant="contained"
										color="primary"
										size="small"
										fullWidth
										onClick={() => setMaterialModalVisible(true)}
										sx={{ textTransform: "none" }}
									>
										Pengambilan Barang
									</MuiButton>
									<MuiButton
										variant="contained"
										color="error"
										size="small"
										fullWidth
										onClick={handlePengembalian}
										sx={{ textTransform: "none" }}
									>
										Pengembalian Barang
									</MuiButton>
								</div>
							</div>

							{/* Search field */}
							<div className="mb-4">
								<input
									type="text"
									placeholder="Search by Inquiry No..."
									value={searchQuery}
									onChange={handleSearchInputChange}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
								/>
							</div>

							{/* Mobile view - title below search */}
							<div className="md:hidden mb-4">
								<h2 className="text-base font-bold text-gray-900 truncate">
									List Pengambilan dan Pengembalian Barang
								</h2>
							</div>

							{isLoading ? (
								<div className="flex justify-center py-4">
									<PulseLoader color="#2563eb" size={10} margin={4} />
								</div>
							) : currentItems.length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									No spareparts found
								</div>
							) : (
								<>
									{/* Desktop view */}
									<div className="hidden md:block overflow-x-auto">
										<table className="min-w-full divide-y divide-gray-200">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														InqNo
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Description
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Reference
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Doc Header Text
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Created Date
													</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{currentItems.map((part) => (
													<tr 
														key={part.inqno} 
														className={`hover:bg-gray-50 ${
															['CRT', 'RAG', 'RIO'].includes(part.type)
																? 'bg-red-50' 
																: 'bg-white'
														}`}
													>
														<td className="px-4 py-2 whitespace-nowrap">
															<div className="text-sm font-medium text-blue-600 hover:text-blue-800">															
																<Link 
																	to={['CRT', 'RAG', 'RIO'].includes(part.type) 
																		? `/editretur/${part.inqno}` 
																		: `/sparepart/${part.inqno}`}
																>
																	{formatInqNo(part.inqno)}
																</Link>
					
															</div>
														</td>
														<td className="px-4 py-2">
															<div className="text-sm text-gray-900">
																{part.ddtext}
															</div>
														</td>
														<td className="px-4 py-2 whitespace-nowrap">
															<div className="text-sm text-gray-900">
																{part.xblnr || "-"}
															</div>
														</td>
														<td className="px-4 py-2 whitespace-nowrap">
															<div className="text-sm text-gray-900">
																{part.bktxt || "-"}
															</div>
														</td>
														<td className="px-4 py-2 whitespace-nowrap">
															<div className="text-sm text-gray-900">
																{formatDateTime(part.erdat, part.erzet)}
															</div>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>

									{/* Mobile view */}
									<div className="md:hidden">
										<div className="space-y-1">
											{currentItems.map((part) => (
												<div
													key={part.inqno}
													className={`border rounded-lg shadow-sm ${
														['CRT', 'RAG', 'RIO'].includes(part.type)
															? 'bg-red-50' 
															: 'bg-white'
													}`}
												>
													<Link
														to={['CRT', 'RAG', 'RIO'].includes(part.type) 
															? `/editretur/${part.inqno}` 
															: `/sparepart/${part.inqno}`}
														className="block px-4 py-2"
													>
														<div className="flex justify-between items-start">
															<div className="flex-1">
																<div className="flex items-center justify-between">
																	<div className="text-sm font-medium text-blue-600">
																		{formatInqNo(part.inqno)}
																	</div>
																	<div className="text-xs text-gray-500">
																		{formatDateTime(part.erdat, part.erzet)}
																	</div>
																</div>
																<div className="text-sm text-gray-900 mt-0.5">
																	{part.ddtext}
																</div>
																{(part.xblnr || part.bktxt) && (
																	<div className="mt-0.5 text-xs">
																		{part.xblnr && (
																			<span className="text-gray-600">
																				Mat Slip: {part.xblnr}
																			</span>
																		)}
																		{part.xblnr && part.bktxt && (
																			<span className="mx-1">•</span>
																		)}
																		{part.bktxt && (
																			<span className="text-gray-600">
																				Doc Text: {part.bktxt}
																			</span>
																		)}
																	</div>
																)}
															</div>
															<ChevronRightIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
														</div>
													</Link>
												</div>
											))}
										</div>
									</div>
								</>
							)}
							{currentItems.length > 0 && (
								<>
									<div className="py-4 flex justify-center">
										<Pagination
											count={totalPages}
											page={currentPage}
											onChange={handlePageChange}
											color="primary"
											showFirstButton
											showLastButton
										/>
									</div>
									<div className="text-sm text-gray-500 text-center pb-4">
										Showing {indexOfFirstItem + 1} to{" "}
										{Math.min(indexOfLastItem, filteredSpareparts.length)} of{" "}
										{filteredSpareparts.length} entries
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</main>

			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				keepMounted
				disablePortal
				aria-labelledby="category-dialog-title"
			>
				<div style={{ padding: "20px" }}>
					<h2 id="category-dialog-title">Select Sparepart Category</h2>
					<div
						style={{ display: "flex", flexDirection: "column", gap: "10px" }}
					>
						<MuiButton
							variant="contained"
							onClick={() => handleCategorySelect("IO")}
							fullWidth
						>
							IO
						</MuiButton>
						<MuiButton
							variant="contained"
							onClick={() => handleCategorySelect("AG")}
							fullWidth
						>
							AG
						</MuiButton>
						<MuiButton
							variant="contained"
							onClick={() => handleCategorySelect("Non Value")}
							fullWidth
						>
							Non Value
						</MuiButton>
					</div>
				</div>
			</Dialog>

			<MaterialSelectionModal
				visible={materialModalVisible}
				onClose={() => setMaterialModalVisible(false)}
				onSubmit={handleMaterialSelect}
				keepMounted
			/>

			<Dialog
				open={returModalOpen}
				onClose={() => setReturModalOpen(false)}
				maxWidth="md"
				fullWidth
				keepMounted
				disablePortal
				aria-labelledby="retur-dialog-title"
			>
				<div className="p-4">
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-xl font-bold" id="retur-dialog-title">
							Select Item for Return
						</h2>
						<button
							onClick={() => setReturModalOpen(false)}
							className="text-gray-500 hover:text-gray-700"
							aria-label="Close dialog"
						>
							×
						</button>
					</div>

					<TextField
						fullWidth
						variant="outlined"
						size="small"
						placeholder="Search by Inquiry No or Material"
						value={returSearchQuery}
						onChange={handleReturSearch}
						className="mb-4"
					/>

					{returLoading ? (
						<div className="flex justify-center py-4">
							<PulseLoader color="#2563eb" size={10} margin={4} />
						</div>
					) : (
						<>
							{/* Desktop view */}
							<div className="hidden md:block">
								<TableContainer component={Paper}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Action</TableCell>
												<TableCell>Inq No</TableCell>
												<TableCell>Description</TableCell>
												<TableCell>Doc Header Text</TableCell>
												<TableCell>Reference</TableCell>
												<TableCell>Created Date</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{currentReturItems.map((item) => (
												<TableRow key={item.inqno}>
													<TableCell>
														<MuiButton
															variant="contained"
															size="small"
															onClick={() => handleReturSelect(item.inqno, item.type)}
														>
															Select
														</MuiButton>
													</TableCell>
													<TableCell>{formatInqNo(item.inqno)}</TableCell>
													<TableCell>{item.ddtext}</TableCell>
													<TableCell>{item.bktxt || "-"}</TableCell>
													<TableCell>{item.xblnr || "-"}</TableCell>
													<TableCell>
														{formatDateTime(item.erdat, item.erzet)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</div>

							{/* Mobile view */}
							<div className="md:hidden">
								<div className="space-y-4">
									{currentReturItems.map((item) => (
										<div
											key={item.inqno}
											className="bg-white border rounded-lg p-4 shadow-sm"
										>
											<div className="flex justify-between items-start mb-2">
												<div className="text-sm font-medium text-blue-600">
													{formatInqNo(item.inqno)}
												</div>
												<div className="text-xs text-gray-500">
													{formatDateTime(item.erdat, item.erzet)}
												</div>
											</div>
											<div className="text-sm text-gray-900 mb-1">
												{item.ddtext}
											</div>
											<div className="text-xs text-gray-600 mb-2">
												{item.bktxt && <div>Doc Header: {item.bktxt}</div>}
												{item.xblnr && <div>Reference: {item.xblnr}</div>}
											</div>
											<div className="flex justify-end">
												<MuiButton
													variant="contained"
													size="small"
													onClick={() => handleReturSelect(item.inqno, item.type)}
												>
													Select
												</MuiButton>
											</div>
										</div>
									))}
								</div>
							</div>

							{/* Add pagination controls */}
							{filteredReturItems.length > 0 && (
								<div className="mt-4">
									<div className="flex justify-center">
										<Pagination
											count={returTotalPages}
											page={returCurrentPage}
											onChange={handleReturPageChange}
											color="primary"
											size="small"
										/>
									</div>
									<div className="text-sm text-gray-500 text-center mt-2">
										Showing {returIndexOfFirstItem + 1} to{" "}
										{Math.min(returIndexOfLastItem, filteredReturItems.length)} of{" "}
										{filteredReturItems.length} entries
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</Dialog>

			{/* Change Password Modal */}
			<Dialog
				open={changePasswordModalOpen}
				onClose={() => {
					if (!isSubmitting && !passwordSuccess) {
						setChangePasswordModalOpen(false);
						setPasswordErrors({});
						setPasswordSuccess('');
						setPasswordForm({
							current_password: '',
							new_password: '',
							new_password_confirmation: ''
						});
						setShowPasswords({
							current: false,
							new: false,
							confirm: false
						});
					}
				}}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					style: { borderRadius: '12px' }
				}}
			>
				<div className="p-8 relative">
					{isSubmitting && (
						<div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
							<PulseLoader color="#2563eb" size={10} margin={4} />
						</div>
					)}
					<div className="flex items-center gap-3 mb-6">
						<div className="bg-blue-100 p-2 rounded-lg">
							<LockIcon className="h-6 w-6 text-blue-600" />
						</div>
						<h2 className="text-xl font-bold text-gray-900">Change Password</h2>
					</div>
					{passwordSuccess ? (
						<div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center justify-center">
							<div className="text-green-600 text-center">
								{passwordSuccess}
								<div className="mt-3">
									<PulseLoader color="#16a34a" size={8} margin={4} />
								</div>
							</div>
						</div>
					) : (
						<form onSubmit={handleChangePassword} className="space-y-5">
							{passwordErrors.general && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-4">
									<div className="text-red-600 text-sm font-medium">
										{passwordErrors.general[0]}
									</div>
								</div>
							)}
							<div className="space-y-4">
								<TextField
									type={showPasswords.current ? "text" : "password"}
									label="Current Password"
									fullWidth
									value={passwordForm.current_password}
									onChange={handlePasswordInputChange('current_password')}
									required
									disabled={isSubmitting}
									error={!!passwordErrors.current_password}
									helperText={passwordErrors.current_password?.[0]}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => togglePasswordVisibility('current')}
													edge="end"
													size="small"
													disabled={isSubmitting}
												>
													{showPasswords.current ? (
														<EyeSlashIcon className="h-5 w-5 text-gray-400" />
													) : (
														<EyeIcon className="h-5 w-5 text-gray-400" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: 'rgb(37 99 235)',
											},
										},
									}}
								/>
								<TextField
									type={showPasswords.new ? "text" : "password"}
									label="New Password"
									fullWidth
									value={passwordForm.new_password}
									onChange={handlePasswordInputChange('new_password')}
									required
									disabled={isSubmitting}
									error={!!passwordErrors.new_password}
									helperText={passwordErrors.new_password?.[0]}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => togglePasswordVisibility('new')}
													edge="end"
													size="small"
													disabled={isSubmitting}
												>
													{showPasswords.new ? (
														<EyeSlashIcon className="h-5 w-5 text-gray-400" />
													) : (
														<EyeIcon className="h-5 w-5 text-gray-400" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: 'rgb(37 99 235)',
											},
										},
									}}
								/>
								<TextField
									type={showPasswords.confirm ? "text" : "password"}
									label="Confirm New Password"
									fullWidth
									value={passwordForm.new_password_confirmation}
									onChange={handlePasswordInputChange('new_password_confirmation')}
									required
									disabled={isSubmitting}
									error={!!passwordErrors.new_password_confirmation}
									helperText={passwordErrors.new_password_confirmation?.[0]}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => togglePasswordVisibility('confirm')}
													edge="end"
													size="small"
													disabled={isSubmitting}
												>
													{showPasswords.confirm ? (
														<EyeSlashIcon className="h-5 w-5 text-gray-400" />
													) : (
														<EyeIcon className="h-5 w-5 text-gray-400" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: 'rgb(37 99 235)',
											},
										},
									}}
								/>
							</div>
							<div className="flex justify-end gap-3 pt-2">
								<MuiButton
									variant="outlined"
									onClick={() => {
										setChangePasswordModalOpen(false);
										setPasswordErrors({});
										setPasswordSuccess('');
										setPasswordForm({
											current_password: '',
											new_password: '',
											new_password_confirmation: ''
										});
										setShowPasswords({
											current: false,
											new: false,
											confirm: false
										});
									}}
									disabled={isSubmitting}
									sx={{
										textTransform: 'none',
										borderColor: 'rgb(37 99 235)',
										color: 'rgb(37 99 235)',
										'&:hover': {
											borderColor: 'rgb(29 78 216)',
											backgroundColor: 'rgb(239 246 255)',
										},
									}}
								>
									Cancel
								</MuiButton>
								<MuiButton
									type="submit"
									variant="contained"
									disabled={isSubmitting}
									sx={{
										textTransform: 'none',
										backgroundColor: 'rgb(37 99 235)',
										'&:hover': {
											backgroundColor: 'rgb(29 78 216)',
										},
									}}
								>
									{isSubmitting ? 'Changing Password...' : 'Change Password'}
								</MuiButton>
							</div>
						</form>
					)}
				</div>
			</Dialog>
		</div>
	);
};

export default Home;
